import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Megaphone,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Brain,
  Star,
  Share2,
  Mail,
  Users,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  Music2,
  Image,
  ExternalLink
} from 'lucide-react';
import { MarketingInfo, SOCIAL_PLATFORMS } from '../../types/companyProfile';

interface MarketingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: MarketingInfo;
  onSave: (data: MarketingInfo) => void;
}

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube,
  tiktok: Music2,
  pinterest: Image
};

const INFLUENCER_PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'twitter', label: 'X (Twitter)' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'blog', label: 'Blog' }
];

const AI_LEARNING_MESSAGES = [
  { threshold: 1, message: 'Pazarlama analizi başlıyor...', subtext: 'Marka stratejinizi anlıyoruz' },
  { threshold: 2, message: 'AI sosyal medya analizi yapıyor...', subtext: 'Kanal performansları değerlendiriliyor' },
  { threshold: 3, message: 'E-posta pazarlama potansiyeli...', subtext: 'Büyüme fırsatları belirleniyor' },
  { threshold: 4, message: 'Mükemmel! Pazarlama profili hazır...', subtext: 'Stratejik öneriler için hazırız' }
];

const STEPS = [
  { id: 'brand', title: 'Marka', icon: Star },
  { id: 'social', title: 'Sosyal Medya', icon: Share2 },
  { id: 'marketing', title: 'Pazarlama', icon: Megaphone }
];

const MarketingInfoModal: React.FC<MarketingInfoModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<MarketingInfo>(initialData || {
    socialMedia: [],
    emailMarketing: { enabled: false },
    influencerMarketing: { enabled: false, platforms: [] }
  });
  const [filledCount, setFilledCount] = useState(0);
  const [aiMessage, setAiMessage] = useState(AI_LEARNING_MESSAGES[0]);
  const [showAiAnimation, setShowAiAnimation] = useState(false);
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>(null);

  // Calculate filled fields
  useEffect(() => {
    let count = 0;
    if (formData.brandAwareness) count++;
    if (formData.socialMedia && formData.socialMedia.length > 0) count++;
    if (formData.emailMarketing?.enabled !== undefined) count++;
    if (formData.influencerMarketing?.enabled !== undefined) count++;
    setFilledCount(count);

    // Update AI message
    const matchingMessage = [...AI_LEARNING_MESSAGES].reverse().find(m => count >= m.threshold);
    if (matchingMessage) {
      setAiMessage(matchingMessage);
    }

    // Show animation when field changes
    if (count > 0) {
      setShowAiAnimation(true);
      const timer = setTimeout(() => setShowAiAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  const setBrandAwareness = (value: number) => {
    setFormData(prev => ({ ...prev, brandAwareness: value }));
  };

  const toggleSocialPlatform = (platformId: string) => {
    const current = formData.socialMedia || [];
    const exists = current.find(s => s.platform === platformId);

    if (exists) {
      // If it has a URL, remove it. Otherwise toggle expand
      if (expandedPlatform === platformId) {
        setFormData(prev => ({
          ...prev,
          socialMedia: current.filter(s => s.platform !== platformId)
        }));
        setExpandedPlatform(null);
      } else {
        setExpandedPlatform(platformId);
      }
    } else {
      // Add platform and expand
      setFormData(prev => ({
        ...prev,
        socialMedia: [...current, { platform: platformId, url: '' }]
      }));
      setExpandedPlatform(platformId);
    }
  };

  const setSocialUrl = (platformId: string, url: string) => {
    const current = formData.socialMedia || [];
    const updated = current.map(s =>
      s.platform === platformId ? { ...s, url } : s
    );
    setFormData(prev => ({ ...prev, socialMedia: updated }));
  };

  const removeSocialPlatform = (platformId: string) => {
    const current = formData.socialMedia || [];
    setFormData(prev => ({
      ...prev,
      socialMedia: current.filter(s => s.platform !== platformId)
    }));
    if (expandedPlatform === platformId) {
      setExpandedPlatform(null);
    }
  };

  const setEmailMarketingEnabled = (enabled: boolean) => {
    setFormData(prev => ({
      ...prev,
      emailMarketing: {
        ...prev.emailMarketing,
        enabled,
        subscriberCount: enabled ? prev.emailMarketing?.subscriberCount : undefined
      }
    }));
  };

  const setSubscriberCount = (count: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      emailMarketing: {
        enabled: prev.emailMarketing?.enabled ?? false,
        ...prev.emailMarketing,
        subscriberCount: count
      }
    }));
  };

  const setInfluencerMarketingEnabled = (enabled: boolean) => {
    setFormData(prev => ({
      ...prev,
      influencerMarketing: {
        ...prev.influencerMarketing,
        enabled,
        platforms: enabled ? prev.influencerMarketing?.platforms : []
      }
    }));
  };

  const toggleInfluencerPlatform = (platform: string) => {
    const current = formData.influencerMarketing?.platforms || [];
    const updated = current.includes(platform)
      ? current.filter(p => p !== platform)
      : [...current, platform];
    setFormData(prev => ({
      ...prev,
      influencerMarketing: {
        enabled: prev.influencerMarketing?.enabled ?? false,
        ...prev.influencerMarketing,
        platforms: updated
      }
    }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onSave(formData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-[#12121a] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-4 border-b border-white/10 bg-gradient-to-r from-pink-500/10 to-rose-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-pink-500/20 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-pink-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Pazarlama Bilgileri</h2>
                <p className="text-sm text-gray-400">Adım {currentStep + 1} / {STEPS.length}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    index === currentStep
                      ? 'bg-pink-500/20 border border-pink-500/50'
                      : index < currentStep
                        ? 'bg-pink-500/10 border border-pink-500/30'
                        : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <StepIcon className={`w-4 h-4 ${
                    index === currentStep
                      ? 'text-pink-400'
                      : index < currentStep
                        ? 'text-pink-400'
                        : 'text-gray-500'
                  }`} />
                  <span className={`text-xs font-medium truncate ${
                    index === currentStep
                      ? 'text-pink-300'
                      : index < currentStep
                        ? 'text-pink-300'
                        : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Learning Banner */}
        <div className={`px-6 py-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-b border-white/5 transition-all duration-500 ${
          showAiAnimation ? 'opacity-100' : 'opacity-70'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`relative ${showAiAnimation ? 'animate-pulse' : ''}`}>
              <Brain className="w-5 h-5 text-purple-400" />
              {showAiAnimation && (
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-bounce" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-300">{aiMessage.message}</p>
              <p className="text-xs text-gray-400">{aiMessage.subtext}</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-white">{filledCount}</span>
              <span className="text-sm text-gray-400"> / 4</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[400px] overflow-y-auto">
          {/* Step 1: Brand Awareness */}
          {currentStep === 0 && (
            <div className="space-y-5">
              <div className="p-4 bg-pink-500/10 rounded-xl border border-pink-500/20">
                <p className="text-sm text-pink-300">
                  💡 Marka bilinirliğinizi değerlendirin. Bu bilgi AI&apos;ın pazarlama stratejisi önerilerini şekillendirmesine yardımcı olur.
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <Star className="w-4 h-4 text-pink-400" />
                  Marka Bilinirliği (1-10)
                </label>
                <div className="space-y-4">
                  {/* Star Rating */}
                  <div className="flex justify-center gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setBrandAwareness(value)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                          formData.brandAwareness && formData.brandAwareness >= value
                            ? 'bg-pink-500/30 text-pink-400 border-2 border-pink-500'
                            : 'bg-white/5 text-gray-500 border-2 border-white/10 hover:border-white/30'
                        }`}
                      >
                        <Star className={`w-5 h-5 ${
                          formData.brandAwareness && formData.brandAwareness >= value ? 'fill-current' : ''
                        }`} />
                      </button>
                    ))}
                  </div>

                  {/* Value Display */}
                  <div className="text-center">
                    <span className="text-4xl font-bold text-white">
                      {formData.brandAwareness || '-'}
                    </span>
                    <span className="text-gray-400 ml-1">/ 10</span>
                  </div>

                  {/* Description */}
                  <div className="text-center text-sm text-gray-400">
                    {!formData.brandAwareness && 'Bir değer seçin'}
                    {formData.brandAwareness && formData.brandAwareness <= 3 && 'Marka bilinirliği düşük - Farkındalık kampanyaları önerilebilir'}
                    {formData.brandAwareness && formData.brandAwareness > 3 && formData.brandAwareness <= 6 && 'Orta düzeyde bilinirlik - Büyüme potansiyeli yüksek'}
                    {formData.brandAwareness && formData.brandAwareness > 6 && formData.brandAwareness <= 8 && 'İyi düzeyde bilinirlik - Sadakat programları önerilebilir'}
                    {formData.brandAwareness && formData.brandAwareness > 8 && 'Güçlü marka! - Premium stratejiler değerlendirilebilir'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Social Media */}
          {currentStep === 1 && (
            <div className="space-y-5">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <Share2 className="w-4 h-4 text-pink-400" />
                  Aktif Sosyal Medya Hesapları
                </label>
                <p className="text-xs text-gray-500 mb-3">Platform seçin ve hesap linkini ekleyin</p>

                <div className="space-y-2">
                  {SOCIAL_PLATFORMS.map((platform) => {
                    const Icon = PLATFORM_ICONS[platform.id] || Share2;
                    const isSelected = formData.socialMedia?.some(s => s.platform === platform.id);
                    const isExpanded = expandedPlatform === platform.id;
                    const currentUrl = formData.socialMedia?.find(s => s.platform === platform.id)?.url || '';

                    return (
                      <div key={platform.id} className="space-y-2">
                        <button
                          type="button"
                          onClick={() => toggleSocialPlatform(platform.id)}
                          className={`w-full p-3 rounded-xl text-left transition-all flex items-center justify-between ${
                            isSelected
                              ? 'bg-pink-500/20 border-2 border-pink-500'
                              : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${isSelected ? 'text-pink-400' : 'text-gray-500'}`} />
                            <span className={`font-medium ${isSelected ? 'text-pink-300' : 'text-white'}`}>
                              {platform.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {currentUrl && (
                              <a
                                href={currentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 hover:bg-white/10 rounded"
                              >
                                <ExternalLink className="w-4 h-4 text-gray-400" />
                              </a>
                            )}
                            {isSelected && <Check className="w-4 h-4 text-pink-400" />}
                          </div>
                        </button>

                        {/* URL Input */}
                        {isSelected && isExpanded && (
                          <div className="flex gap-2 pl-4">
                            <input
                              type="url"
                              value={currentUrl}
                              onChange={(e) => setSocialUrl(platform.id, e.target.value)}
                              placeholder={`https://${platform.id}.com/hesabiniz`}
                              className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/50 transition-all"
                            />
                            <button
                              type="button"
                              onClick={() => removeSocialPlatform(platform.id)}
                              className="px-3 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors text-sm"
                            >
                              Kaldır
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Email & Influencer Marketing */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Email Marketing */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <Mail className="w-4 h-4 text-pink-400" />
                  E-posta Pazarlama Yapıyor musunuz?
                </label>
                <div className="flex gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setEmailMarketingEnabled(true)}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      formData.emailMarketing?.enabled === true
                        ? 'bg-green-500/20 border-2 border-green-500 text-green-300'
                        : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    {formData.emailMarketing?.enabled === true && <Check className="w-4 h-4 inline mr-2" />}
                    Evet
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmailMarketingEnabled(false)}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      formData.emailMarketing?.enabled === false
                        ? 'bg-red-500/20 border-2 border-red-500 text-red-300'
                        : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    {formData.emailMarketing?.enabled === false && <X className="w-4 h-4 inline mr-2" />}
                    Hayır
                  </button>
                </div>

                {/* Subscriber Count */}
                {formData.emailMarketing?.enabled && (
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">E-posta Abone Sayısı</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.emailMarketing?.subscriberCount || ''}
                      onChange={(e) => setSubscriberCount(parseInt(e.target.value) || undefined)}
                      placeholder="Örn: 5000"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/50 transition-all"
                    />
                  </div>
                )}
              </div>

              {/* Influencer Marketing */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <Users className="w-4 h-4 text-pink-400" />
                  Influencer Marketing Yapıyor musunuz?
                </label>
                <div className="flex gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setInfluencerMarketingEnabled(true)}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      formData.influencerMarketing?.enabled === true
                        ? 'bg-green-500/20 border-2 border-green-500 text-green-300'
                        : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    {formData.influencerMarketing?.enabled === true && <Check className="w-4 h-4 inline mr-2" />}
                    Evet
                  </button>
                  <button
                    type="button"
                    onClick={() => setInfluencerMarketingEnabled(false)}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      formData.influencerMarketing?.enabled === false
                        ? 'bg-red-500/20 border-2 border-red-500 text-red-300'
                        : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    {formData.influencerMarketing?.enabled === false && <X className="w-4 h-4 inline mr-2" />}
                    Hayır
                  </button>
                </div>

                {/* Influencer Platforms */}
                {formData.influencerMarketing?.enabled && (
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Hangi platformlarda?</label>
                    <div className="flex flex-wrap gap-2">
                      {INFLUENCER_PLATFORMS.map((platform) => (
                        <button
                          key={platform.value}
                          type="button"
                          onClick={() => toggleInfluencerPlatform(platform.value)}
                          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                            formData.influencerMarketing?.platforms?.includes(platform.value)
                              ? 'bg-pink-500/20 border-2 border-pink-500 text-pink-300'
                              : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                          }`}
                        >
                          {formData.influencerMarketing?.platforms?.includes(platform.value) && (
                            <Check className="w-3.5 h-3.5 inline mr-1" />
                          )}
                          {platform.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a12]/50 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentStep === 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Vazgeç
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              {currentStep === STEPS.length - 1 ? (
                <>
                  <Check className="w-4 h-4" />
                  Kaydet
                </>
              ) : (
                <>
                  İleri
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MarketingInfoModal;
